/**
 * Phase 5: XP and levelling, gold splits and loot rolls (pure 5e SRD rules; the server applies them).
 */
import { ABILITIES, abilityMod, proficiencyForLevel } from "./dnd.js";
import { newId, totalLevel } from "./sheet.js";
/** 5e SRD: total XP needed to reach each level (index = level). */
export const XP_THRESHOLDS = [
    0, 0, 300, 900, 2700, 6500, 14000, 23000, 34000, 48000, 64000,
    85000, 100000, 120000, 140000, 165000, 195000, 225000, 265000, 305000, 355000,
];
export const MAX_LEVEL = 20;
/** The level a character with this much XP has reached (1-20). */
export function levelForXp(xp) {
    let lvl = 1;
    for (let l = 2; l <= MAX_LEVEL; l++)
        if (xp >= XP_THRESHOLDS[l])
            lvl = l;
    return lvl;
}
/** XP needed for the next level (null at 20). */
export const xpForNextLevel = (level) => (level >= MAX_LEVEL ? null : XP_THRESHOLDS[level + 1]);
/** SRD class hit dice (lower-case class names). */
export const CLASS_HIT_DIE = {
    barbarian: 12, fighter: 10, paladin: 10, ranger: 10,
    bard: 8, cleric: 8, druid: 8, monk: 8, rogue: 8, warlock: 8, artificer: 8,
    sorcerer: 6, wizard: 6,
};
export const hitDieFor = (className) => CLASS_HIT_DIE[className.trim().toLowerCase()] ?? null;
/** HP gained for a level when taking the average: half the die + 1, plus CON mod (at least 1). */
export const averageHpGain = (die, conMod) => Math.max(1, die / 2 + 1 + conMod);
/** Spell slots by caster level for full casters (SRD "Multiclass Spellcaster" table = full-caster progression). */
export const FULL_CASTER_SLOTS = [
    [],
    [2], [3], [4, 2], [4, 3], [4, 3, 2], [4, 3, 3], [4, 3, 3, 1], [4, 3, 3, 2], [4, 3, 3, 3, 1], [4, 3, 3, 3, 2],
    [4, 3, 3, 3, 2, 1], [4, 3, 3, 3, 2, 1], [4, 3, 3, 3, 2, 1, 1], [4, 3, 3, 3, 2, 1, 1], [4, 3, 3, 3, 2, 1, 1, 1],
    [4, 3, 3, 3, 2, 1, 1, 1], [4, 3, 3, 3, 2, 1, 1, 1, 1], [4, 3, 3, 3, 3, 1, 1, 1, 1], [4, 3, 3, 3, 3, 2, 1, 1, 1],
    [4, 3, 3, 3, 3, 2, 2, 1, 1],
];
/** Warlock Pact Magic by warlock level: [slots, slot level]. */
export function pactMagic(warlockLevel) {
    const l = Math.max(0, Math.min(MAX_LEVEL, warlockLevel));
    if (l === 0)
        return { max: 0, level: 0 };
    const max = l === 1 ? 1 : l <= 10 ? 2 : l <= 16 ? 3 : 4;
    const level = Math.min(5, Math.ceil(l / 2));
    return { max, level };
}
/** Which spellcasting progression a class (and subclass) follows. */
export function casterKind(className, subclass = "") {
    const c = className.trim().toLowerCase();
    const s = subclass.trim().toLowerCase();
    if (["bard", "cleric", "druid", "sorcerer", "wizard"].includes(c))
        return "full";
    if (["paladin", "ranger", "artificer"].includes(c))
        return "half";
    if (c === "warlock")
        return "pact";
    if ((c === "fighter" && s.includes("eldritch knight")) || (c === "rogue" && s.includes("arcane trickster")))
        return "third";
    return "none";
}
/** All class levels of a character: the main class plus multiclass entries (subclass applies to the main class). */
export function classLevels(className, level, d) {
    const out = [];
    if (className.trim())
        out.push({ className: className.trim(), level, subclass: d.subclass });
    for (const m of d.multiclass)
        if (m.className.trim() && m.level > 0)
            out.push({ className: m.className.trim(), level: m.level, subclass: "" });
    return out;
}
/**
 * Spell slots (index 0 = 1st level) and pact magic for these class levels, by the SRD rules: a single-class
 * half or third caster rounds its caster level up (from level 2 / 3); multiclassing adds full + half/2 + third/3 (rounded down).
 * Returns null when no class casts spells (leave a hand-entered sheet alone).
 */
export function spellSlotsFor(classes) {
    let full = 0, half = 0, third = 0, warlock = 0, casters = 0;
    for (const c of classes) {
        const k = casterKind(c.className, c.subclass ?? "");
        if (k === "full")
            full += c.level;
        else if (k === "half")
            half += c.level;
        else if (k === "third")
            third += c.level;
        else if (k === "pact")
            warlock += c.level;
        if (k !== "none" && k !== "pact")
            casters++;
    }
    if (!full && !half && !third && !warlock)
        return null;
    let casterLevel;
    if (casters <= 1) {
        casterLevel = full || (half >= 2 ? Math.ceil(half / 2) : 0) || (third >= 3 ? Math.ceil(third / 3) : 0);
    }
    else {
        casterLevel = full + Math.floor(half / 2) + Math.floor(third / 3);
    }
    const row = FULL_CASTER_SLOTS[Math.min(MAX_LEVEL, casterLevel)] ?? [];
    const slots = Array.from({ length: 9 }, (_, i) => row[i] ?? 0);
    return { slots, pact: pactMagic(warlock) };
}
/** Can this character level up now (XP reached, or a milestone level waiting)? */
export function canLevelUp(c, progression) {
    const total = totalLevel(c.level, c.details);
    if (total >= MAX_LEVEL)
        return false;
    return progression === "milestone" ? (c.pendingLevels ?? 0) > 0 : levelForXp(c.details.xp) > total;
}
/**
 * Apply one level to a sheet (pure; the server rolls the HP first). Updates the class level (main, multiclass or a
 * new class), max/current HP, hit dice, proficiency bonus, spell slots (when a known caster class), ASI, and appends
 * new features and spells. Returns the new sheet and a one-line summary.
 */
export function applyLevelUp(sheet, input, hpGain, die) {
    const d = structuredClone(sheet.details);
    const name = input.className.trim();
    let level = sheet.level;
    let className = sheet.className;
    let classLevel;
    if (!className.trim() || className.trim().toLowerCase() === name.toLowerCase()) {
        if (!className.trim())
            className = name;
        level += 1;
        classLevel = level;
    }
    else {
        const mc = d.multiclass.find((m) => m.className.trim().toLowerCase() === name.toLowerCase());
        if (mc) {
            mc.level += 1;
            classLevel = mc.level;
        }
        else {
            d.multiclass.push({ className: name, level: 1 });
            classLevel = 1;
        }
    }
    if (input.subclass?.trim() && className.trim().toLowerCase() === name.toLowerCase())
        d.subclass = input.subclass.trim().slice(0, 80);
    const gain = Math.max(1, Math.round(hpGain));
    const hd = d.hitDice.find((h) => h.die === die);
    if (hd) {
        hd.total += 1;
        hd.remaining += 1;
    }
    else {
        d.hitDice.push({ die, total: 1, remaining: 1 });
    }
    const abilities = { ...sheet.abilities };
    const inc = input.abilityIncreases ?? {};
    let budget = 2;
    // In the order the player listed them, 2 points in total.
    for (const a of Object.keys(inc).filter((k) => ABILITIES.includes(k))) {
        const want = Math.max(0, Math.min(budget, Math.round(Number(inc[a] ?? 0)) || 0));
        if (!want)
            continue;
        const before = abilities[a];
        abilities[a] = Math.min(20, before + want);
        budget -= abilities[a] - before;
    }
    // CON increase raises max HP retroactively (1 per level per +1 modifier).
    const conDelta = abilityMod(abilities.con) - abilityMod(sheet.abilities.con);
    const newTotal = totalLevel(level, d);
    const retro = conDelta * (newTotal - 1);
    const slots = spellSlotsFor(classLevels(className, level, d));
    if (slots) {
        d.spellcasting.slots = d.spellcasting.slots.map((sl, i) => {
            const max = slots.slots[i] ?? 0;
            return { max, used: Math.min(sl.used, max) };
        });
        if (slots.pact.max)
            d.spellcasting.pact = { level: slots.pact.level, max: slots.pact.max, used: Math.min(d.spellcasting.pact.used, slots.pact.max) };
    }
    for (const f of input.features ?? []) {
        if (!f.name.trim())
            continue;
        d.features.push({ id: newId(), kind: "class", name: f.name.trim().slice(0, 100), description: (f.description ?? "").slice(0, 2000), maxUses: null, used: 0, recharge: "none" });
    }
    for (const sp of input.spells ?? []) {
        if (!sp.name.trim() || d.spellcasting.spells.some((x) => x.name.toLowerCase() === sp.name.trim().toLowerCase()))
            continue;
        d.spellcasting.spells.push({
            id: newId(), level: Math.max(0, Math.min(9, Math.round(sp.level) || 0)), name: sp.name.trim().slice(0, 100), prepared: true,
            school: "", castingTime: "", range: "", components: "", duration: "", concentration: false, ritual: false, description: "",
        });
    }
    const maxHp = Math.max(1, sheet.maxHp + gain + retro);
    const out = {
        ...sheet, className, level, abilities, details: d, maxHp, currentHp: Math.max(0, Math.min(maxHp, sheet.currentHp + gain + retro)),
        proficiencyBonus: proficiencyForLevel(newTotal),
    };
    const summary = `${sheet.name} reached level ${newTotal}` + (d.multiclass.length ? ` (${name} ${classLevel})` : "") + ` and gained ${gain + retro} HP`;
    return { sheet: out, summary, newTotal, classLevel };
}
// ------------------------------------------------------------------ coins
export const COIN_CP = { pp: 1000, gp: 100, ep: 50, sp: 10, cp: 1 };
export const coinsToCp = (c) => Object.keys(COIN_CP).reduce((s, k) => s + Math.max(0, Math.round(Number(c[k] ?? 0)) || 0) * COIN_CP[k], 0);
/** Copper into gp / sp / cp (no pp or ep, so a share reads like "3 GP 5 SP"). */
export function cpToCoins(cp) {
    let left = Math.max(0, Math.floor(cp));
    const gp = Math.floor(left / 100);
    left -= gp * 100;
    const sp = Math.floor(left / 10);
    return { pp: 0, gp, ep: 0, sp, cp: left - sp * 10 };
}
export function formatCoins(c) {
    const parts = ["pp", "gp", "ep", "sp", "cp"].filter((k) => (c[k] ?? 0) > 0).map((k) => `${c[k]} ${k.toUpperCase()}`);
    return parts.join(" ") || "0 GP";
}
/** Split a pile evenly: each share (in gp/sp/cp) and the copper left over that doesn't divide. */
export function splitCoins(totalCp, people) {
    if (people <= 0)
        return { share: cpToCoins(0), shareCp: 0, leftoverCp: totalCp };
    const shareCp = Math.floor(totalCp / people);
    return { share: cpToCoins(shareCp), shareCp, leftoverCp: totalCp - shareCp * people };
}
/** Add coins to a purse. */
export const addCoins = (purse, add) => ({
    pp: purse.pp + add.pp, gp: purse.gp + add.gp, ep: purse.ep + add.ep, sp: purse.sp + add.sp, cp: purse.cp + add.cp,
});
/**
 * Who claims a loot item: Need beats Want; claimants in the best tier roll a d20 (ties re-roll among the tied).
 * `roll` returns 1-20. Returns null when nobody claimed it.
 */
export function lootWinner(claims, roll) {
    const tier = claims.some((c) => c.choice === "need") ? "need" : claims.some((c) => c.choice === "want") ? "want" : null;
    if (!tier)
        return null;
    let contenders = claims.filter((c) => c.choice === tier);
    const rolls = new Map(contenders.map((c) => [c, []]));
    for (let guard = 0; guard < 50; guard++) {
        if (contenders.length === 1)
            break;
        let best = 0;
        const results = contenders.map((c) => {
            const r = roll();
            rolls.get(c).push(r);
            best = Math.max(best, r);
            return { c, r };
        });
        contenders = results.filter((x) => x.r === best).map((x) => x.c);
    }
    return { winner: contenders[0], tier, rolls: [...rolls.entries()].map(([claim, r]) => ({ claim, rolls: r })) };
}
//# sourceMappingURL=progress.js.map